import { useEffect, useState } from 'react';
import { api } from '../../api';
import TileCard from '../components/TileCard';

/**
 * StatCardTile — big-number tile for a single headline metric.
 *
 * Props:
 *   title      (required) — card title
 *   source     (required) — key into SOURCE_LOADERS below
 *   href       (optional) — makes the whole card a link
 *   accent     (optional) — 'gold' | 'success' | 'warn' | 'danger'
 *
 * Sources resolve to { value, sublabel }. Add a new source by registering a
 * loader function. Keeps this tile dumb — all data wiring is in the map.
 */

const SOURCE_LOADERS = {
  expenses_month_total: async () => {
    try {
      const d = await api.getExpenseStats();
      // /api/expenses/stats returns { pending_amount, approved_amount, mtd_total }.
      // mtd_total = all month-to-date rows that aren't denied, which is what
      // Matt's Expenses page treats as "this month's spend."
      const total = d?.mtd_total ?? 0;
      const pending = d?.pending_amount ?? 0;
      const sublabel = pending > 0
        ? `MTD · ${formatCurrency(pending)} pending`
        : 'Month-to-date';
      return { value: formatCurrency(total), sublabel };
    } catch {
      return { value: '—', sublabel: 'Could not load' };
    }
  },
  driver_weekly_tows: async () => {
    try {
      const d = await api.getDashboardDriverStats();
      return { value: String(d?.weekly_tows ?? 0), sublabel: 'This week' };
    } catch {
      return { value: '—', sublabel: 'Could not load' };
    }
  },
  damage_fund_balance: async () => {
    try {
      const { account } = await api.getMyDamageFund();
      if (!account) return { value: '—', sublabel: 'No fund account yet' };
      const bal = Number(account.balance) || 0;
      const value = `${bal < 0 ? '-' : ''}$${Math.abs(bal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const sublabel = account.at_cap
        ? 'At cap — deductions paused'
        : account.underwater
          ? 'Underwater'
          : 'Escrow balance';
      return { value, sublabel };
    } catch {
      return { value: '—', sublabel: 'Could not load' };
    }
  },
};

function formatCurrency(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

export default function StatCardTile({ title, source, href, accent }) {
  const [state, setState] = useState({ loading: true, value: null, sublabel: null });

  useEffect(() => {
    let cancelled = false;
    const loader = SOURCE_LOADERS[source] || (async () => ({ value: '—', sublabel: 'Unknown source' }));
    loader().then(result => {
      if (!cancelled) setState({ loading: false, ...result });
    });
    return () => { cancelled = true; };
  }, [source]);

  return (
    <TileCard title={title} loading={state.loading} actionHref={href} actionLabel={href ? 'Go →' : null}>
      <div className="dash-statcard">
        <div className={`dash-statcard-value ${accent ? `accent-${accent}` : ''}`}>
          {state.value}
        </div>
        {state.sublabel && <div className="dash-statcard-sublabel">{state.sublabel}</div>}
      </div>
    </TileCard>
  );
}
